import React, { useState, useEffect } from 'react';
import { Dialog, Flex, Text, TextField, Button } from '@radix-ui/themes';
import { useCreateDiscordServerMutation } from '../../graphql/mutations/CreateDiscordServer.generated';
import { useUpdateDiscordServerMutation } from '../../graphql/mutations/UpdateDiscordServer.generated';

interface DiscordServerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  server?: {
    id: string;
    name: string;
    ownerUserId?: string | null;
    ownerTeamId?: string | null;
  } | null;
  onSuccess?: () => void;
}

export default function DiscordServerDialog({
  open,
  onOpenChange,
  server,
  onSuccess,
}: DiscordServerDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [createDiscordServer, { loading: creating }] =
    useCreateDiscordServerMutation();
  const [updateDiscordServer, { loading: updating }] =
    useUpdateDiscordServerMutation();

  const isEdit = !!server;
  const loading = creating || updating;

  useEffect(() => {
    if (server) {
      setName(server.name);
    } else {
      setName('');
    }
    setError(null);
  }, [server, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Server name is required');
      return;
    }

    try {
      if (isEdit) {
        await updateDiscordServer({
          variables: {
            input: {
              id: server.id,
              name: name.trim(),
            },
          },
          refetchQueries: ['GetDiscordServers'],
        });
      } else {
        await createDiscordServer({
          variables: {
            input: {
              name: name.trim(),
            },
          },
          refetchQueries: ['GetDiscordServers'],
        });
      }

      onOpenChange(false);
      setName('');
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: 450 }}>
        <Dialog.Title>
          {isEdit ? 'Edit Discord Server' : 'Add Discord Server'}
        </Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {isEdit
            ? 'Update the Discord server configuration.'
            : 'Add a new Discord server integration.'}
        </Dialog.Description>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Server Name
              </Text>
              <TextField.Root
                placeholder="My Discord Server"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </label>

            {error && (
              <Text color="red" size="2">
                {error}
              </Text>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray" disabled={loading}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
